import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import './TalentfixrJobs.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const JobDetailPage = () => {
  const { slug } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('detail'); // detail | form | success
  const [applied, setApplied] = useState(null);

  useEffect(() => {
    fetch(`${API}/jobs/${slug}`)
      .then(r => { if (!r.ok) throw new Error('404'); return r.json(); })
      .then(data => { setJob(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="tf-jobs"><div className="tf-container" style={{ textAlign: 'center', padding: 60 }}><p className="tf-muted">Laden...</p></div></div>;
  if (!job) return (
    <div className="tf-jobs"><div className="tf-container" style={{ textAlign: 'center', padding: 60 }}>
      <p style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>Vacature niet gevonden</p>
      <Link to="/jobs">Alle jobs bekijken</Link>
    </div></div>
  );

  if (view === 'success') return <SuccessScreen applied={applied} job={job} />;
  if (view === 'form') return <RegistrationForm job={job} onBack={() => setView('detail')} onSuccess={(data) => { setApplied(data); setView('success'); }} />;

  const isClosed = job.status === 'closed';
  const badges = [job.hours, ...(job.features || [])].filter(Boolean);

  return (
    <div className="tf-jobs" data-testid="job-detail-page">
      <div className="tf-container">
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none' }}>
            <img src="/assets/logo.png" alt="TalentFixr" style={{ width: 44, height: 44, objectFit: 'contain' }} />
            <span style={{ fontSize: 24, fontWeight: 800, color: '#163e32', letterSpacing: 1, textDecoration: 'none' }}>TALENTFIXR.</span>
          </Link>
          <Link to="/jobs" style={{ color: '#163e32', fontWeight: 600, fontSize: 14 }}>Alle jobs</Link>
        </div>

        <button type="button" className="tf-back" onClick={() => window.history.back()} data-testid="back-btn">
          &larr; Alle jobs
        </button>

        {isClosed ? (
          <div className="tf-closed-card">
            <p style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Deze vacature is ingevuld.</p>
            <Link to="/jobs" className="tf-primary" style={{ display: 'inline-block', maxWidth: 260, textDecoration: 'none', textAlign: 'center' }}>
              Bekijk andere jobs
            </Link>
          </div>
        ) : (
          <>
            {/* Compact green banner */}
            <section className="tf-hero">
              <p className="tf-eyebrow">Via Talentfixr &middot; {job.location}</p>
              <h1>{job.title}</h1>
              <p>{job.intro}</p>
              <div className="tf-tags">
                {badges.map(b => <span className="tf-tag" key={b}>{b}</span>)}
              </div>
            </section>

            {/* Interest panel */}
            <section className="tf-panel">
              <h2>Interesse in deze job?</h2>
              <p className="tf-muted">Laat weten dat je interesse hebt. Een cv is niet nodig.</p>
              <button type="button" className="tf-primary" onClick={() => setView('form')} data-testid="interest-btn">
                Ik heb interesse &rarr;
              </button>
              <p className="tf-note">Talentfixr ontvangt je reactie en neemt die in behandeling.</p>
            </section>
          </>
        )}
      </div>
    </div>
  );
};

// === Registration Form ===
const RegistrationForm = ({ job, onBack, onSuccess }) => {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const params = new URLSearchParams(window.location.search);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (pending) return;
    const fd = new FormData(e.currentTarget);
    const payload = {
      job_id: job.id,
      name: String(fd.get('name') || '').trim(),
      phone: String(fd.get('phone') || '').trim(),
      email: String(fd.get('email') || '').trim(),
      specialism: fd.get('specialism') || '',
      location_preference: String(fd.get('location') || '').trim(),
      experience: fd.get('experience') || '',
      screening_answer: fd.get('screeningAnswer') || '',
      consent_share_data: fd.get('submitToTalentfixr') === 'on',
      consent_text_shown: 'Ik wil dat Talentfixr mijn interesse in deze vacature behandelt.',
      utm_source: params.get('utm_source') || null,
      utm_medium: params.get('utm_medium') || null,
      utm_campaign: params.get('utm_campaign') || null,
    };

    if (!payload.name || payload.name.length < 2) { setError('Vul je naam in (minstens 2 tekens)'); return; }
    if (!payload.phone) { setError('Vul je gsm-nummer in'); return; }
    if (!payload.email) { setError('Vul je e-mailadres in'); return; }
    if (!payload.specialism) { setError('Kies je specialisatie'); return; }
    if (!payload.experience) { setError('Kies je ervaring'); return; }
    if (!payload.screening_answer) { setError('Beantwoord de screeningvraag'); return; }
    if (!payload.consent_share_data) { setError('Bevestig dat je je gegevens wil delen'); return; }

    setPending(true);
    setError('');

    try {
      const res = await fetch(`${API}/jobs/${job.id}/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || 'Er ging iets mis.'); setPending(false); return; }
      onSuccess(data);
    } catch {
      setError('Je reactie kon niet worden verstuurd. Controleer je gegevens en probeer opnieuw.');
      setPending(false);
    }
  };

  return (
    <div className="tf-jobs" data-testid="interest-form-page">
      <div className="tf-container">
        <button type="button" className="tf-back" onClick={onBack}>
          &larr; Terug naar de job
        </button>

        <form className="tf-panel tf-form" onSubmit={handleSubmit}>
          <p className="tf-eyebrow">{job.title} &middot; {job.location}</p>
          <h2>Even kennismaken.</h2>
          <p className="tf-muted">Geen cv. Alleen wat we nodig hebben om je reactie te behandelen.</p>

          <div className="tf-field">
            <label htmlFor="tf-name">Naam</label>
            <input id="tf-name" name="name" autoComplete="name" placeholder="Je naam" required data-testid="interest-name" />
          </div>

          <div className="tf-field">
            <label htmlFor="tf-email">E-mailadres</label>
            <input id="tf-email" name="email" type="email" autoComplete="email" placeholder="jij@voorbeeld.be" required data-testid="interest-email" />
          </div>

          <div className="tf-field">
            <label htmlFor="tf-phone">Gsm-nummer</label>
            <input id="tf-phone" name="phone" type="tel" autoComplete="tel" placeholder="+32 ..." required data-testid="interest-phone" />
          </div>

          <div className="tf-field">
            <label htmlFor="tf-specialism">Specialisatie</label>
            <select id="tf-specialism" name="specialism" defaultValue="" required data-testid="interest-specialism">
              <option value="" disabled>Kies je specialisatie</option>
              {['Mechanica', 'Elektriciteit', 'Elektromechanica', 'HVAC', 'Werkvoorbereiding', 'Productie-engineering', 'Andere'].map(v => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </div>

          <div className="tf-field">
            <label htmlFor="tf-location">Woonplaats of postcode</label>
            <input id="tf-location" name="location" placeholder="Bijvoorbeeld Kortrijk of 8500" required data-testid="interest-location" />
          </div>

          <div className="tf-field">
            <label htmlFor="tf-experience">Ervaring in deze functie</label>
            <select id="tf-experience" name="experience" defaultValue="" required data-testid="interest-experience">
              <option value="" disabled>Kies je ervaring</option>
              <option value="none">Nog geen ervaring</option>
              <option value="less_than_2">Minder dan 2 jaar</option>
              <option value="2_to_5">2 tot 5 jaar</option>
              <option value="more_than_5">Meer dan 5 jaar</option>
            </select>
          </div>

          {job.screening_question && (
            <div className="tf-field">
              <label htmlFor="tf-screening">{job.screening_question}</label>
              <select id="tf-screening" name="screeningAnswer" defaultValue="" required data-testid="interest-screening">
                <option value="" disabled>Maak een keuze</option>
                <option value="yes">Ja</option>
                <option value="no">Nee</option>
              </select>
            </div>
          )}

          <label className="tf-check">
            <input type="checkbox" name="submitToTalentfixr" required data-testid="interest-consent" />
            <span>Ik wil dat Talentfixr mijn interesse in deze vacature behandelt.</span>
          </label>

          <p className="tf-note">
            Talentfixr ontvangt je reactie en neemt die in behandeling.{' '}
            <Link to="/privacy" target="_blank">Lees hoe we je gegevens gebruiken.</Link>
          </p>

          {error && <p className="tf-error" role="alert" data-testid="interest-error">{error}</p>}

          <button type="submit" className="tf-primary" disabled={pending} data-testid="interest-submit">
            {pending ? 'Even verwerken...' : 'Verstuur mijn interesse \u2192'}
          </button>
        </form>
      </div>
    </div>
  );
};

// === Success Screen ===
const SuccessScreen = ({ applied, job }) => (
  <div className="tf-jobs">
    <div className="tf-container tf-success">
      <div className="tf-success-icon">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#f5f0e6" strokeWidth="2.5" strokeLinecap="round"><path d="M20 6L9 17l-5-5"/></svg>
      </div>
      <h2 style={{ fontSize: 22, fontWeight: 800, marginBottom: 8 }}>Je interesse is verstuurd!</h2>
      <p className="tf-muted" style={{ marginBottom: 4 }}>{applied.job_title} &middot; {applied.job_location}</p>
      <p className="tf-note" style={{ marginBottom: 32 }}>Talentfixr neemt je reactie in behandeling.</p>

      <p style={{ fontSize: 16, fontWeight: 700, marginBottom: 20 }}>Wil je ook andere passende jobs ontdekken?</p>
      <Link to="/jobs" className="tf-primary" style={{ display: 'block', maxWidth: 320, margin: '0 auto', textDecoration: 'none', textAlign: 'center' }} data-testid="success-more-jobs">
        Ja, ontdek meer jobs
      </Link>
      <Link to="/" className="tf-outline-btn" data-testid="success-only-this">
        Voorlopig alleen deze job
      </Link>
    </div>
  </div>
);

export default JobDetailPage;
